import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./queryList.css";
import { useGetDraftEmbeddingModelsQuery } from "../../service/apiSlices/sematicModel";
import ModelListItem from "./modelListItem";
import EmptyContent from "./emptyContent";
import { TOKENS } from "../../utilities/Constants/Constants";
import { setCheckedList, setDisplayItems } from "../../service/apiSlices/staticApi";
import WorkFlowPageLinkDeciderModal from "../WorkbentchModel/workbentchModel";

interface DraftQueryProps {
  activeView: string;
  debouncedSearchText: string;
  perpage?: number;
  isTabActive: boolean;
  alignQuery: any
}

interface ModelItem {
  id: string;
  workspace_name: string;
  created_on: Date;
  file_type: string;
  secret_key: string;
  [key: string]: any;
}

interface ModalState {
  state: boolean;
  Id: string;
}

const DraftQuery: React.FC<DraftQueryProps> = ({
  activeView,
  alignQuery,
  debouncedSearchText,
  perpage: initialPerpage = 24,
  isTabActive,
}) => {
  const { selectedAction, checkedList } = useSelector(
    (state: any) => state.static
  );
  const connection_Id: string | null = sessionStorage.getItem(
    TOKENS.CONNECTION_ID
  );

  const dispatch = useDispatch();

  const [allItems, setAllItems] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerpage] = useState<number>(initialPerpage);
  const [page, setPage] = useState(1);
  const [isScrolling, setIsScrolling] = useState(false);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ModelItem | null>(null);
  const [modalOpen, setModalOpen] = useState<ModalState>({
    state: false,
    Id: "",
  });

  const params = {
    search: debouncedSearchText,
    page,
    limit: perPage,
    connection_key: connection_Id,
  };

  const {
    data: modelsData,
    refetch: modelRefetch,
    isFetching: isDataLoading,
  } = useGetDraftEmbeddingModelsQuery(params, { skip: !isTabActive });

  // Reset when search or view changes
  useEffect(() => {
    if (!isTabActive) return;

    modelRefetch();
  }, [debouncedSearchText,alignQuery, isTabActive]);

  useEffect(() => {
    if (!containerRef) return;

    const handleScroll = (e: any) => {
      if (isScrolling) {
        return;
      }

      const target = e.target as HTMLDivElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (perPage < totalItems) {
          setIsScrolling(true);
          setPerpage(prev => {
            const newVal = Math.min(prev + 10, totalItems);
            setTimeout(() => setIsScrolling(false), 500);
            return newVal;
          });
        }
      }
    };

    containerRef.addEventListener("scroll", handleScroll);

    return () => {
      containerRef.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef,
    isScrolling,
    totalItems,
    perPage
  ]);


  useEffect(() => {
    if (modelsData?.data) {
      setAllItems(modelsData?.data);
      dispatch(setDisplayItems(modelsData?.data?.length))
      setTotalItems(modelsData?.total_count);
      setIsLoading(false);
    }
  }, [modelsData?.data, isTabActive])

  const handleListCheck = useCallback(
    (item: any, checked: boolean) => {
      dispatch(setCheckedList({ id: item.secret_key, checked, view: "draft_semantic" }));
    },
    [dispatch, modelsData?.data]
  );

  const handleItemClick = useCallback(
    (item: any) => {
      if (item) {
        setSelectedItem(item);
        if (item.file_type === "draft_co_pilot") {
          setModalOpen({ state: true, Id: item.secret_key });
        }
      }
    },
    [modelsData?.data]
  );

  const handleModalChange = (): void => {
    setModalOpen({ state: false, Id: "" });
  };

  return (
    <div className="models-list-container-model overflow-auto" ref={el => setContainerRef(el)}>
      {allItems.length === 0 &&
        <EmptyContent isLoading={isDataLoading} itemsLength={allItems.length} />}
      {!isDataLoading && allItems.length > 0 && (
        <div className="row modelsList-data-wrapper-row-list g-3">
          {allItems?.map((item: ModelItem) => (
            <ModelListItem
              key={item.id}
              id={item.id}
              item={item}
              name={item.workspace_name}
              dateTime={item.created_on}
              isChecked={checkedList.draft_semantic.includes(item.secret_key)}
              showCheckbox={selectedAction === "check"}
              onCheckboxClick={handleListCheck}
              onItemClick={handleItemClick}
            />
          ))}
        </div>)}
      {selectedItem && modalOpen.state && (
        <WorkFlowPageLinkDeciderModal
          isVisible={modalOpen}
          onChange={handleModalChange}
          onGetURL={() => []}
          selectedItem={selectedItem}
          config={{ showChat: false }}
        />
      )}
    </div>
  );
};

export default memo(DraftQuery);
